// Bomb the Hill's tutorial (#201): a guided first run. Each lesson waits
// until you've done it (the last two are just read), on the tutorial's own
// road (`TUTORIAL_COURSE`: the warm-up's wide rows far apart and no moving
// traffic, with a crosswalk and a side street laid out early for their
// lessons; one you've passed by then waits for the next the road brings),
// and the run carries on as a real one after "You're ready". A run that ends partway puts you back at the top on the
// same lesson. Skippable, and remembered in localStorage once done or
// skipped, so the lobby only offers it by itself the first time.
//
// Sim-free and three.js-free: `Game` feeds it what the rider is doing each
// frame (`HillLessonView`), and `ui/HillHud.tsx` draws the lesson. The text
// is written with pad buttons; the HUD renames them for the input you're on
// (`namedFor`).

export type HillLessonId = "pick" | "carve" | "wobble" | "gap" | "ollie" | "crossing" | "junction" | "walls" | "ready";

export interface HillLesson {
  id: HillLessonId;
  text: string;
  /** A lesson that's only read moves on by itself after this long (ms); the rest wait until you've done it. */
  readMs?: number;
}

export const HILL_LESSONS: readonly HillLesson[] = [
  { id: "pick", text: "Pick your ride, then ✕ to drop in" },
  { id: "carve", text: "Carve: steer left, then right. Every carve scrubs speed" },
  { id: "wobble", text: "Let the speed bar run into the red and feel the wobble, then carve to calm it" },
  { id: "gap", text: "Find the way through the parked cars" },
  { id: "ollie", text: "✕ to ollie: hop a pothole, or pop off a ramp. Add □ in the air for a kickflip" },
  { id: "crossing", text: "People cross at the zebra crossings: find a way past them" },
  { id: "junction", text: "Side streets have lights and cross traffic: go on green, or find a gap" },
  { id: "walls", text: "Walls end the run, unless you wallride along them", readMs: 4000 },
  { id: "ready", text: "You're ready! Keep going and see how far you get", readMs: 3000 },
];

/** A carve past this, each way, counts for the carve lesson. */
export const LESSON_CARVE = 0.5;
/** The wobbles past this count as having felt them... */
export const LESSON_WOBBLE = 0.25;
/** ...and back under this, still riding, as calmed. */
export const LESSON_CALM = 0.05;
/** Past a row (or a crosswalk or side street's far side) by this much (m) counts as through it. */
const PAST_ROW = 3;

/** What the rider's doing, as the tutorial reads it each frame. */
export interface HillLessonView {
  /** At the top picking a ride. */
  picking: boolean;
  /** The run's over (a bail or a hit), waiting to go back to the top. */
  ended: boolean;
  /** The carve, -1 .. 1. */
  carve: number;
  /** The speed wobbles, 0 .. 1. */
  wobble: number;
  /** Off the ground: in the air. */
  air: boolean;
  /** How far down the road (m), in a z that doesn't jump when the road moves back up (course z). */
  z: number;
  /** The next row of parked vehicles below `z`, in the same z; null if none is laid out yet. */
  nextRow: number | null;
  /** The far side of the next crosswalk below `z`, in the same z; null if none is laid out yet. */
  nextCrossing: number | null;
  /** The far side of the next side street below `z`, in the same z; null if none is laid out yet. */
  nextJunction: number | null;
}

/** What the HUD shows of the tutorial. */
export interface HillLessonHud {
  id: HillLessonId;
  text: string;
  /** Which lesson, from 1, of `of`. */
  step: number;
  of: number;
  /** The run ended (or you're back at the top) partway through a lesson: drop in again to carry on. */
  again: boolean;
}

export class HillTutorial {
  index = 0;
  private elapsed = 0;
  private left = false;
  private right = false;
  private felt = false;
  private wasAir = false;
  /** The row, crosswalk or side street the lesson's watching (its z). */
  private row: number | null = null;
  private last: HillLessonHud | null = null;

  get done(): boolean {
    return this.index >= HILL_LESSONS.length;
  }

  get lesson(): HillLesson | null {
    return HILL_LESSONS[this.index] ?? null;
  }

  /** One frame of riding, `ms` long. True when it finished a lesson. */
  step(v: HillLessonView, ms: number): boolean {
    const lesson = this.lesson;
    if (!lesson) return false;
    if (lesson.readMs !== undefined) {
      this.elapsed += ms;
      return this.elapsed >= lesson.readMs && this.next();
    }
    if (v.picking || v.ended) {
      // Nothing counts at the top or once the run's over; what was half done starts again.
      this.felt = this.wasAir = false;
      this.row = null;
      return lesson.id === "pick" && !v.picking && this.next();
    }
    switch (lesson.id) {
      case "pick":
        return this.next();
      case "carve":
        if (v.carve <= -LESSON_CARVE) this.left = true;
        if (v.carve >= LESSON_CARVE) this.right = true;
        return this.left && this.right && this.next();
      case "wobble":
        if (v.wobble >= LESSON_WOBBLE) this.felt = true;
        return this.felt && v.wobble <= LESSON_CALM && this.next();
      case "gap":
        return this.past(v.nextRow, v.z);
      case "crossing":
        return this.past(v.nextCrossing, v.z);
      case "junction":
        return this.past(v.nextJunction, v.z);
      case "ollie":
        if (v.air) this.wasAir = true;
        return this.wasAir && !v.air && this.next();
      default:
        return false;
    }
  }

  skip(): void {
    this.index = HILL_LESSONS.length;
  }

  /** The lesson for the HUD, the same object until it changes; null once done. */
  hud(v: Pick<HillLessonView, "picking" | "ended">): HillLessonHud | null {
    const lesson = this.lesson;
    if (!lesson) return (this.last = null);
    const again = lesson.id !== "pick" && lesson.readMs === undefined && (v.picking || v.ended);
    const prev = this.last;
    if (prev && prev.id === lesson.id && prev.again === again) return prev;
    return (this.last = { id: lesson.id, text: lesson.text, step: this.index + 1, of: HILL_LESSONS.length, again });
  }

  /** Through the thing at `ahead` (the first one seen since the lesson or the run started), still riding. */
  private past(ahead: number | null, z: number): boolean {
    if (this.row === null) this.row = ahead;
    return this.row !== null && z > this.row + PAST_ROW && this.next();
  }

  private next(): true {
    this.index++;
    this.elapsed = 0;
    this.felt = this.wasAir = false;
    this.row = null;
    return true;
  }
}

const KEY = "kickflip.hillTutorial";

/** Whether the hill's tutorial has been done or skipped on this device. */
export function hillTutorialSeen(store: Storage | null): boolean {
  try {
    return store?.getItem(KEY) === "done";
  } catch {
    return false;
  }
}

export function markHillTutorialSeen(store: Storage | null): void {
  try {
    store?.setItem(KEY, "done");
  } catch {
    // Storage refused (the artifact frame): it's offered again next time.
  }
}
